/**
 * Интернационализация интерфейса (B1).
 *
 * Словари грузятся по требованию: десять языков в одном бандле — это лишние
 * сотни килобайт, из которых человек прочитает один.
 *
 * Правила склонения берутся из `Intl.PluralRules`, а не пишутся руками:
 * у русского три формы, у румынского свои, у финского другие — угадывать это
 * в коде значит ошибаться в каждом втором языке.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DEFAULT_LANGUAGE, isSupported, supportedLanguages } from './languages';
import en from './locales/en.json';

const STORAGE_KEY = 'alterium.lang';

const loaded = new Set<string>(['en']);

/** Догрузить словарь языка. Английский уже в бандле — он запасной. */
export async function loadLanguage(code: string): Promise<void> {
  // Браузер отдаёт `ru-RU`, а словарь называется `ru`.
  const base = code.split('-')[0] ?? code;
  if (loaded.has(base) || !isSupported(base)) return;

  const dictionary = (await import(`./locales/${base}.json`)) as {
    default: Record<string, unknown>;
  };
  i18n.addResourceBundle(base, 'translation', dictionary.default, true, true);
  loaded.add(base);
}

/**
 * Проставить `lang` на `<html>`.
 *
 * В разметке он задан статически и без этого остаётся таким навсегда — то есть
 * врёт при любом языке, кроме одного. Врёт он не человеку, а скринридеру,
 * который выберет по нему произношение, и автопереводчику браузера, который
 * решит, переводить ли страницу.
 */
function applyDocumentLanguage(code: string): void {
  document.documentElement.lang = code.split('-')[0] ?? code;
}

export async function changeLanguage(code: string): Promise<void> {
  await loadLanguage(code);
  await i18n.changeLanguage(code);
  applyDocumentLanguage(code);

  // Сервер тоже должен знать язык: по нему уходят письма. Без этого выбор
  // жил бы только в localStorage — то есть на одном устройстве, — а письма
  // приходили бы на языке по умолчанию, какой бы язык человек ни выбрал.
  //
  // Незалогиненному сохранять некуда, и это не ошибка: язык переживёт вход
  // в localStorage, а на сервер уедет при следующей смене.
  void persistLanguage(code);
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Приватный режим: язык не переживёт перезагрузку, но переключится сейчас.
  }
}

/** Тихо: неудача сохранения не должна мешать переключению языка. */
async function persistLanguage(code: string): Promise<void> {
  try {
    const { accountApi } = await import('../api/account');
    await accountApi.update({ locale: code.split('-')[0] ?? code });
  } catch {
    // Не авторизован или сеть недоступна — язык уже переключился.
  }
}

export async function setupI18n(): Promise<typeof i18n> {
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: { en: { translation: en } },
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: supportedLanguages,
      // Английский показывается, пока грузится выбранный язык, и когда
      // ключа в переводе нет — пустой экран хуже чужого языка.
      nonExplicitSupportedLngs: true,
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: STORAGE_KEY,
        caches: [],
      },
      interpolation: { escapeValue: false },
      returnNull: false,
    });

  // `language` — то, что выбрал пользователь; `resolvedLanguage` — то, для чего
  // уже есть словарь, и на старте это всегда английский. Грузить надо первое,
  // иначе выбранный язык не догрузится никогда.
  const requested = i18n.language ?? DEFAULT_LANGUAGE;
  if (requested !== 'en') {
    await loadLanguage(requested);
    // После добавления словаря язык надо переустановить: i18next уже решил,
    // что показывает запасной, и сам об этом не передумает.
    await i18n.changeLanguage(requested);
  }

  applyDocumentLanguage(i18n.resolvedLanguage ?? DEFAULT_LANGUAGE);

  return i18n;
}

export { i18n };
export * from './languages';
